import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Typography } from "@mui/material";
import { CheckCircleOutline, ChevronLeft, InfoOutlined, Phone, WarningAmberOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import {
  Button,
  Callout,
  ConsentRow,
  InfoBanner,
  OtpInput,
  PageHeader,
  SkeletonTextoLegal,
  StepActions,
  TypographyLegal,
  TypographyOtpHelp,
} from "@components/ui_v2";
import { ConfirmationDialog } from "@components/ConexionSat/ConfirmationDialog";
import { RootState } from "@store/store";
import { useRequestPhoneCodeMutation, useValidatePhoneCodeMutation } from "@api/onboarding";
import { useLazyGetPersonByIdQuery } from "@api/personasApi";
import { useLazyGetAddressQuery } from "@api/address";
import { useLazyGetColonyByIdQuery } from "@api/catalogs";
import { BuroValidatorRequestPF, useBuroValidatorMutation } from "@api/sat_apis_new/satApsi";
import { ApiError, PersonaStructure } from "@interfaces/response";
import { ADDRESS_CODES, PATHS_EXPEDIENTE_V2, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { formatPhoneNumber, getRemainingTime, truncateText } from "@helpers/funciones";
import useTextosLegales from "@hooks/useTextosLegales";

const TIME_LIMIT = 120;

const PaperInfoIncompleta = ({ onBack }: { onBack: () => void }) => (
  <>
    <Paper variant="lendia_paper" sx={{ textAlign: "center", p: "40px 28px" }}>
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          bgcolor: "#FFF8EC",
          color: "warning.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          mb: 2,
        }}
      >
        <WarningAmberOutlined sx={{ fontSize: 26 }} />
      </Box>
      <Typography
        sx={{
          fontFamily: "Outfit",
          fontSize: 18,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "primary.dark",
        }}
      >
        Información personal incompleta
      </Typography>
      <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", lineHeight: 1.55, mt: 1 }}>
        Para continuar con la consulta al buró de crédito, primero debes completar la información requerida en la
        sección <strong style={{ color: "inherit" }}>Llena tu información personal</strong>.
      </Typography>
    </Paper>
    <Box sx={{ display: "flex", mt: 4 }}>
      <Button
        tone="ghost"
        startIcon={<ChevronLeft fontSize="small" />}
        onClick={onBack}
        data-testid="consulta-buro-pf-back-button"
      >
        Regresar
      </Button>
    </Box>
  </>
);

export const ConsultaBuroPf = () => {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const goBack = () => navigate(PATHS_EXPEDIENTE_V2.expediente.path);

  const application = useSelector((state: RootState) => state.register.application);

  const { textosLegales, cargandoTextos } = useTextosLegales("ConsultaBuroPf");

  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode, { isLoading: isRequestingCode }] = useRequestPhoneCodeMutation();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getAddress] = useLazyGetAddressQuery();
  const [getColony] = useLazyGetColonyByIdQuery();
  const [buroValidator] = useBuroValidatorMutation();

  const [person, setPerson] = useState<PersonaStructure | null>(null);
  const [isLoadingPerson, setIsLoadingPerson] = useState(true);
  const [infoIncompleta, setInfoIncompleta] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [modalOtp, setModalOtp] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [justResent, setJustResent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [otpIsValid, setOtpIsValid] = useState(false);
  const [validatedCode, setValidatedCode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [lastCodeRequest, setLastCodeRequest] = useState<string | null>(null);

  const otpConfirmCorrecto = modalOtp.length === 4 && modalOtp === validatedCode;
  const autorizado = termsAccepted;

  const personTieneInfoCompleta = (p: PersonaStructure): boolean =>
    !!(
      p.name &&
      p.last_name &&
      p.rfc &&
      p.curp &&
      p.phone_number &&
      p.code_phone_number &&
      p.birth_date &&
      p.birth_state &&
      p.sex
    );

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const t = setTimeout(() => setSecondsRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsRemaining]);

  const sendCode = async (p: PersonaStructure) => {
    const countryCode = p.code_phone_number;
    const phoneNumber = p.phone_number;
    if (!countryCode || !phoneNumber) return;

    const secondsToWait = getRemainingTime(lastCodeRequest || "", TIME_LIMIT);
    if (secondsToWait > 0) {
      setSecondsRemaining(secondsToWait);
      return;
    }

    setLastCodeRequest(dayjs().toISOString());
    try {
      await requestPhoneCode({ country_code: countryCode, phone_number: phoneNumber }).unwrap();
    } catch (error) {
      if ((error as ApiError)?.status !== 429) {
        setLastCodeRequest(null);
      }
      throw error;
    }

    setSecondsRemaining(TIME_LIMIT);
    setJustResent(true);
    setTimeout(() => setJustResent(false), 4000);
  };

  const handleSendCode = async () => {
    if (!person || isRequestingCode) return;
    try {
      setIsLoading(true);
      setRateLimited(false);
      await sendCode(person);
      setCodeSent(true);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
      } else {
        Sentry.captureException(error, { extra: { component: "ConsultaBuroPf-send" } });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!person || otpIsValid || isRequestingCode) return;
    try {
      setIsLoading(true);
      setRateLimited(false);
      await sendCode(person);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
        return;
      }
      Sentry.captureException(error, { extra: { component: "ConsultaBuroPf-resend" } });
    } finally {
      setIsLoading(false);
    }
  };

  const validateCode = async () => {
    if (otpIsValid) {
      setErrorMessage("");
      return;
    }
    if (otp.length < 4 || !person?.code_phone_number || !person?.phone_number) return;

    try {
      setIsLoading(true);
      setErrorMessage("");

      await validatePhoneCode({
        country_code: person.code_phone_number,
        phone_number: person.phone_number,
        code: otp,
      }).unwrap();

      setOtpIsValid(true);
      setValidatedCode(otp);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 404 || apiError?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        setOtp("");
        return;
      }
      Sentry.captureException(apiError, { extra: { component: "ConsultaBuroPf-validate", values: { otp } } });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    validateCode();
  }, [otp]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      if (!personId) return;
      const p = await getPersonById(personId).unwrap();
      setPerson(p);

      if (!personTieneInfoCompleta(p)) {
        setInfoIncompleta(true);
        setIsLoadingPerson(false);
        return;
      }

      if (otpIsValid && validatedCode) {
        setOtp(validatedCode);
        setCodeSent(true);
        return;
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ConsultaBuroPf-init" } });
    } finally {
      setIsLoadingPerson(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [personId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConsentClick = () => {
    if (!otpIsValid || termsAccepted) return;
    setModalOtp("");
    setShowConfirmModal(true);
  };

  const handleAcceptConsent = () => {
    if (!otpConfirmCorrecto) return;
    setTermsAccepted(true);
    setShowConfirmModal(false);
    setModalOtp("");
  };

  const handleContinue = async () => {
    if (!otpIsValid || !termsAccepted || !person) return;

    setIsLoading(true);
    try {
      if (!person.is_credit_bureau_authorization_pf_sended || !person.is_credit_bureau_authorization_pf_correct) {
        const addresses = await getAddress({ personId: person.id }).unwrap();
        const domicilio = addresses.find((a) => a.address_type?.code === ADDRESS_CODES.particular.code) ?? addresses[0];
        if (!domicilio) throw new Error("No se encontró el domicilio de la persona");

        let colonyName = domicilio.other_neighborhood || "";
        if (domicilio.colonia_id) {
          const responseColony = await getColony({ colonyId: domicilio.colonia_id }).unwrap();
          colonyName = responseColony.name || "";
        }

        const buroPayload: BuroValidatorRequestPF = {
          person_Type: "pf",
          data: {
            rfc: person.rfc,
            curp: person.curp,
            transaction_number: String(application.id),
            application_code: application.company_application_code,
            legal_name: truncateText(person.name, 26),
            first_name: truncateText(person.name, 26),
            second_name: "",
            father_last_name: truncateText(person.last_name, 26),
            mother_last_name: truncateText(person.last_name_2, 26),
            reenter_NIP: true,
            authorization_label_response: true,
            loan_application_id: String(application.id),
            person_id: person.id,
            is_endorsement: false,
            address: {
              street: domicilio.street,
              exteriorNumber: truncateText(domicilio.no_ext || "", 20),
              interiorNumber: truncateText(domicilio.no_int || "", 20),
              state: domicilio.state?.name ?? "",
              municipality: domicilio.locality?.name ?? "",
              locality: truncateText(colonyName, 26),
              postalCode: domicilio.cp,
            },
          },
        };

        await buroValidator(buroPayload).unwrap();
      }

      goBack();
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 409) {
        goBack();
        return;
      }
      Sentry.captureException(error, { extra: { component: "ConsultaBuroPf-consulta" } });
    } finally {
      setIsLoading(false);
    }
  };

  const phoneDisplay =
    person?.code_phone_number && person?.phone_number
      ? `(${person.code_phone_number}) ${formatPhoneNumber(person.phone_number)}`
      : "—";

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente · Buró de crédito"
        title="Autoriza la consulta al buró de crédito"
        subtitle={
          codeSent
            ? "Ingresa el código de 4 dígitos que enviamos por SMS y WhatsApp."
            : "Te enviaremos un código de verificación al número de teléfono registrado."
        }
      />

      {infoIncompleta ? (
        <PaperInfoIncompleta onBack={goBack} />
      ) : !codeSent ? (
        <>
          <Paper variant="lendia_paper" sx={{ p: "28px 24px" }}>
            <InfoBanner
              icon={<Phone fontSize="small" />}
              label="Teléfono celular"
              value={isLoadingPerson ? "—" : phoneDisplay}
            />
            {rateLimited && (
              <Box sx={{ mt: 2 }}>
                <Callout tone="warn">
                  Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un asesor.
                </Callout>
              </Box>
            )}
          </Paper>
          <StepActions
            onBack={goBack}
            onNext={handleSendCode}
            nextLabel="Enviar código"
            nextDisabled={isLoadingPerson || isLoading || isRequestingCode}
            backTestId="consulta-buro-pf-back-button"
            nextTestId="consulta-buro-pf-send-button"
          />
        </>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <InfoBanner
              icon={<Phone fontSize="small" />}
              label="Teléfono celular"
              value={phoneDisplay}
            />
          </Box>

          {autorizado ? (
            <Paper variant="lendia_paper" sx={{ textAlign: "center", p: "32px 24px" }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  bgcolor: "#ECFDF3",
                  color: "success.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <CheckCircleOutline sx={{ fontSize: 26 }} />
              </Box>
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  color: "primary.dark",
                }}
              >
                Autorización registrada
              </Typography>
              <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", lineHeight: 1.55, mt: 1, mb: 2.5 }}>
                Hemos confirmado tu autorización para consultar el Buró de Crédito de Personas.
              </Typography>
              <ConsentRow
                checked
                onChange={() => undefined}
                disabled
                label={
                  cargandoTextos ? (
                    <SkeletonTextoLegal lineas={2} />
                  ) : (
                    <TypographyLegal
                      type="autorizacionBuro"
                      taxSystemType={TAX_SYSTEM_TYPE.PF.code}
                      texto={textosLegales.buro_persona?.check_buro?.texto}
                    />
                  )
                }
              />
            </Paper>
          ) : (
            <>
              <Paper
                variant="lendia_paper"
                sx={{
                  textAlign: "center",
                  p: "36px 28px 28px",
                  boxShadow: "0 1px 2px rgba(14, 31, 58, 0.06), 0 1px 3px rgba(14, 31, 58, 0.04)",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <OtpInput
                    length={4}
                    value={otp}
                    onChange={setOtp}
                    size="lg"
                    disabled={isLoading || isLoadingPerson || otpIsValid}
                  />
                </Box>

                {errorMessage && (
                  <Box sx={{ mt: 2 }}>
                    <Callout tone="warn">{errorMessage}</Callout>
                  </Box>
                )}

                {rateLimited && (
                  <Box sx={{ mt: 2 }}>
                    <Callout tone="warn">
                      Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un
                      asesor.
                    </Callout>
                  </Box>
                )}

                {otpIsValid ? (
                  <Typography sx={{ mt: 2, fontSize: 13, fontWeight: 500, color: "success.main" }}>
                    Código verificado
                  </Typography>
                ) : (
                  <TypographyOtpHelp
                    mt={2}
                    justResent={justResent}
                    secondsRemaining={secondsRemaining}
                    onResend={handleResendCode}
                    resendDisabled={isLoading || isLoadingPerson}
                    testIdPrefix="consulta-buro-pf"
                  />
                )}
              </Paper>

              <Paper variant="lendia_paper" sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
                <ConsentRow
                  bare
                  checked={false}
                  onChange={handleConsentClick}
                  disabled={!otpIsValid || isLoading || cargandoTextos}
                  label={
                    cargandoTextos ? (
                      <SkeletonTextoLegal lineas={2} />
                    ) : (
                      <TypographyLegal
                        type="autorizacionBuro"
                        taxSystemType={TAX_SYSTEM_TYPE.PF.code}
                        texto={textosLegales.buro_persona?.check_buro?.texto}
                      />
                    )
                  }
                />
                <Callout tone="info" icon={<InfoOutlined sx={{ fontSize: 16 }} />}>
                  <Box component="span" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>
                    {cargandoTextos ? (
                      <SkeletonTextoLegal lineas={3} />
                    ) : (
                      <TypographyLegal type="avisoEvaluacion" texto={textosLegales.buro_persona?.aviso?.texto} />
                    )}
                  </Box>
                </Callout>
              </Paper>
            </>
          )}
          <StepActions
            onBack={goBack}
            onNext={handleContinue}
            nextLabel="Continuar"
            nextDisabled={!autorizado || isLoading || isLoadingPerson}
            backTestId="consulta-buro-pf-back-button"
            nextTestId="consulta-buro-pf-continue-button"
          />
        </>
      )}

      <ConfirmationDialog
        open={showConfirmModal}
        title={textosLegales.buro_persona?.modal_buro?.titulo?.trim() || "Consulta a Buró de Crédito"}
        onClose={() => setShowConfirmModal(false)}
        onAccept={handleAcceptConsent}
        closeLabel="Cancelar"
        acceptLabel="Acepto y autorizo"
        acceptDisabled={!otpConfirmCorrecto}
      >
        <Typography component="div" sx={{ fontSize: 13.5, color: "tokens.textSecondary", lineHeight: 1.65, mb: 2 }}>
          {cargandoTextos ? (
            <SkeletonTextoLegal lineas={8} />
          ) : (
            <TypographyLegal
              type="modalBuro"
              taxSystemType={TAX_SYSTEM_TYPE.PF.code}
              parrafos={textosLegales.buro_persona?.modal_buro?.parrafos}
              secciones={textosLegales.buro_persona?.modal_buro?.secciones}
            />
          )}
        </Typography>
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "primary.dark", mb: 1.25 }}>
            Confirma tu identidad reingresando el código OTP
          </Typography>
          <OtpInput length={4} value={modalOtp} onChange={setModalOtp} />
          {modalOtp.length === 4 && !otpConfirmCorrecto && (
            <Typography sx={{ fontSize: 12.5, color: "error.main", mt: 1 }}>
              Código incorrecto. Inténtalo de nuevo.
            </Typography>
          )}
        </Box>
      </ConfirmationDialog>
    </Box>
  );
};
